/** Tile-owned inland swamp rendering: shared flora materials/wind, exact
 * near/far botanical templates, and existing scatter teardown/collision. */
import * as T from 'three';
import {rngFromString} from '@hyperlight/shared';
import type {FloraLibrary} from './library';
import type {ClutterSets} from './clutter';
import type {InlandWetlandData,InlandWetlandKind} from './inlandWetlandData';
import {buildWetlandReeds} from './cypress';
import {floraHeight,floraTime} from './material';
import {makePooledFloraMesh} from './materialPool';
import {prepareScatterBounds} from './scatterLod';
import type {CoastalCollider} from './coastalHabitat';

type WetClutter=ClutterSets&{inlandReeds?:T.BufferGeometry[];inlandReedsCoarse?:T.BufferGeometry[]};
interface Stem {p:[number,number,number];radius:number}
export function cypressColliders(geo:T.BufferGeometry,matrix:T.Matrix4,size:number):CoastalCollider[] {
  const data=geo.userData.cypress,result:CoastalCollider[]=[],stem=data.stem as Stem[];
  const a=new T.Vector3(),b=new T.Vector3(),mid=new T.Vector3(),axis=new T.Vector3(),delta=new T.Vector3();
  for(let i=0;i<stem.length-1;i++){
    a.fromArray(stem[i].p).applyMatrix4(matrix);b.fromArray(stem[i+1].p).applyMatrix4(matrix);delta.copy(b).sub(a);axis.copy(a).normalize();
    const along=delta.dot(axis),side=Math.sqrt(Math.max(0,delta.lengthSq()-along*along)),steps=Math.max(1,Math.ceil(Math.abs(along)/1.8),Math.ceil(side/.3));
    for(let j=0;j<steps;j++){
      const t=(j+.5)/steps;mid.copy(a).lerp(b,t);axis.copy(mid).normalize();const h=Math.abs(delta.dot(axis))/steps;
      const r=Math.max(T.MathUtils.lerp(stem[i].radius,stem[i+1].radius,j/steps),T.MathUtils.lerp(stem[i].radius,stem[i+1].radius,(j+1)/steps))*size+side/(2*steps)+.035;
      mid.addScaledVector(axis,-h*.5-.04);result.push({x:mid.x,y:mid.y,z:mid.z,r,h:h+.08,minAlong:0});
    }
  }
  // Visible knee spikes remain solid while the spaces between the flares stay passable.
  for(const root of data.roots){mid.fromArray(root.knee).applyMatrix4(matrix);result.push({x:mid.x,y:mid.y,z:mid.z,r:root.width*.40*size+.03,h:root.height*size,minAlong:0});}
  return result;
}
export function mountInlandWetland(data:InlandWetlandData,library:FloraLibrary,clutter:ClutterSets,far=false) {
  const meshes:T.InstancedMesh[]=[],colliders:CoastalCollider[]=[];
  const groups=new Map<string,{geo:T.BufferGeometry;coarse?:T.BufferGeometry;kind:InlandWetlandKind;items:{matrix:T.Matrix4;evidence:object}[]}>();
  const sets=clutter as WetClutter,Y=new T.Vector3(0,1,0),up=new T.Vector3(),pos=new T.Vector3(),q=new T.Quaternion(),spin=new T.Quaternion(),scale=new T.Vector3();
  for(const p of data){
    if(far&&p.kind!=='cypress')continue;
    const key=`${p.kind}:${p.variant}`;let group=groups.get(key);
    if(!group){
      let geo:T.BufferGeometry,coarse:T.BufferGeometry|undefined;
      if(p.kind==='cypress'){geo=(far?library.variantsFar('cypress','summer',2):library.variants('cypress','summer',2))[p.variant];coarse=far?undefined:library.variantsFar('cypress','summer',2)[p.variant];}
      else if(p.kind==='reeds'){
        if(!sets.inlandReeds){sets.inlandReeds=[0,1].map(i=>buildWetlandReeds(rngFromString(`inland-rushes:1:${i}`)));sets.inlandReedsCoarse=[0,1].map(i=>buildWetlandReeds(rngFromString(`inland-rushes:1:${i}`),true));}
        geo=sets.inlandReeds[p.variant];coarse=sets.inlandReedsCoarse![p.variant];
      }else{geo=clutter.shrub[p.variant];coarse=clutter.shrubCoarse[p.variant];}
      groups.set(key,group={geo,coarse,kind:p.kind,items:[]});
    }
    up.fromArray(p.normal);q.setFromUnitVectors(Y,up).multiply(spin.setFromAxisAngle(Y,p.yaw));pos.fromArray(p.dir).multiplyScalar(p.radius);
    scale.set(p.size,p.size*(p.kind==='cover'?.40:1),p.size);
    const matrix=new T.Matrix4().compose(pos,q,scale);group.items.push({matrix,evidence:{...p,position:pos.toArray()}});
    if(!far&&p.kind==='cypress')colliders.push(...cypressColliders(group.geo,matrix,p.size));
  }
  for(const group of groups.values()){
    const fadeFar=far?0:group.kind==='cypress'?950:group.kind==='cover'?300:260,fadeNear=far?950:0;
    const height=group.kind==='cypress'?group.geo.userData.cypress.height:floraHeight(group.geo);
    const mesh=makePooledFloraMesh(group.geo,group.items.length,{birth:floraTime(),fadeFar,fadeNear,height});
    group.items.forEach((p,i)=>mesh.setMatrixAt(i,p.matrix));mesh.instanceMatrix.needsUpdate=true;
    Object.assign(mesh.userData,{inlandWetland:group.kind,inlandWetlandFar:far,inlandWetlandCover:group.kind!=='cypress',wetlandCover:group.kind!=='cypress',fadeFar,fadeNear,
      coverEvidence:group.items.map(p=>p.evidence),scatterLodNear:group.kind==='cypress'?240:95,scatterLodFar:group.kind==='cypress'?320:135});
    mesh.castShadow=!far&&group.kind==='cypress';mesh.receiveShadow=true;mesh.visible=false;prepareScatterBounds(mesh,group.coarse);meshes.push(mesh);
  }
  return {meshes,colliders};
}
